import React, { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import styles from "./TemplateEditor.module.scss";
import { Form } from "react-final-form";
import { Radios, Checkboxes } from "mui-rff";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import {
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ImageGalleryV2 from "../ImageGallery/imageGalleryV2";

const loadCKEditorScript = () =>
  new Promise((resolve) => {
    if (window.CKEDITOR) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src="/ckeditor/ckeditor.js"]');
    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.async = true;
    script.onload = () => resolve();
    document.body.appendChild(script);
  });

const TemplateEditor = ({ selectedTemplate, onSave, refetch }) => {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);

  const [category, setCategory] = useState("");
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [templateName, setTemplateName] = useState(selectedTemplate?.name || "");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const [userLevel, setUserLevel] = useState("user");
  const [showGallery, setShowGallery] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const { data: categoryData, isLoading: isCategoryLoading } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axios.post("/treatment_category");
      return data;
    }
  );

  const { data: treatmentPlans, isLoading: isTreatmentPlansLoading } = useQuery(
    ["treatmentPlans"],
    async () => {
      const { data } = await axios.get(`/treatment`);
      return data;
    },
    {
      onSuccess: (data) => {
        const plans = data.treatments.map((plan) => ({
          ...plan,
          label: plan.description,
          value: plan.id,
        }));
        setTreatmentOptions(plans);
      },
    }
  );

  useEffect(() => {
    const fetchUserLevel = async () => {
      try {
        const { data } = await axios.get("/user");
        setUserLevel(data?.level || "user");
      } catch (error) {
        console.error("Error fetching user level:", error);
        setUserLevel("user");
      }
    };

    fetchUserLevel();
  }, []);

  useEffect(() => {
    const fetchIsAdmin = async () => {
      try {
        const { data } = await axios.get("/user/is_admin");
        setIsAdmin(Boolean(data?.data ?? data));
      } catch (error) {
        console.error("Error fetching admin status:", error);
      }
    };

    fetchIsAdmin();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      setIsGlobal(selectedTemplate.level === "global");
      setTemplateName(selectedTemplate.name || "");
      setCategory(selectedTemplate.category_id || "");
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate && selectedTemplate.treatment_ids?.length) {
      const preselectedTreatments = treatmentOptions.filter((option) =>
        selectedTemplate.treatment_ids.includes(option.value)
      );
      setSelectedTreatments(preselectedTreatments);
    } else {
      setSelectedTreatments([]);
    }
  }, [selectedTemplate, treatmentOptions]);

  useEffect(() => {
    let mounted = true;

    const initEditor = async () => {
      if (!editorRef.current) return;

      await loadCKEditorScript();

      if (!mounted || !window.CKEDITOR) return;

      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy(true);
        editorInstanceRef.current = null;
      }

      const instance = window.CKEDITOR.replace(editorRef.current, {
        height: "800px",
        resize_enabled: true,
        resize_dir: "vertical",
      });

      editorInstanceRef.current = instance;

      instance.on("instanceReady", () => {
        setEditorReady(true);
        instance.setData(selectedTemplate?.body || "");
      });
    };

    initEditor();

    return () => {
      mounted = false;
      setEditorReady(false);

      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy(true);
        editorInstanceRef.current = null;
      }
    };
  }, [selectedTemplate?.id]);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    onSave?.(templateName, e.target.value, selectedTreatments);
  };

  const handleTreatmentChange = (_, value) => {
    setSelectedTreatments(value);
    onSave?.(templateName, category, value);
  };

  const handleTemplateNameChange = (e) => {
    setTemplateName(e.target.value);
    onSave?.(e.target.value, category, selectedTreatments);
  };

  const uploadCallback = (file) => {
    const token = axios.defaults.headers.common.Authorization;

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);

      axios
        .post(`/consent_clause/upload_image_to_s3`, formData)
        .then(() => {
          return axios.post("consent_clause/upload_user_media_to_s3", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: token,
            },
          });
        })
        .then((response) => {
          toast.success("Content added to the image gallery");
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const validateConsentClause = async (payload, id) => {
    try {
      const response = await axios.put("consent_clause/validation_check", {
        id,
        treatment_ids: payload.treatment_ids,
        level: payload.level,
      });

      return response.data;
    } catch (error) {
      console.error("Error in validation check:", error);
      return {
        result: false,
        message: "Validation failed",
        data: [],
      };
    }
  };

  const handleSave = async (values) => {
    const editorInstance = window.CKEDITOR?.instances?.editor1;
    const editorContent = editorInstance ? editorInstance.getData() : "";

    const base64Regex = /<img[^>]+src="data:image\/[^;]+;base64,[^"]+"[^>]*>/g;
    const base64Images = editorContent.match(base64Regex);

    let updatedContent = editorContent;

    if (base64Images) {
      for (const base64ImgTag of base64Images) {
        const base64Data = base64ImgTag.match(
          /src="(data:image\/[^;]+;base64,[^"]+)"/
        )?.[1];

        if (base64Data) {
          try {
            const blob = await fetch(base64Data).then((res) => res.blob());
            const file = new File([blob], "uploaded_image.png", {
              type: blob.type,
            });
            const uploadResponse = await uploadCallback(file);
            const imageUrl = uploadResponse.data;
            updatedContent = updatedContent.replace(base64Data, imageUrl);
          } catch (error) {
            console.error("Error uploading image:", error);
          }
        }
      }
    }

    const finalLevel = userLevel === "practice" ? values.level : "user";

    const payload = {
      body: updatedContent,
      category_id: values.category || null,
      is_cloned: false,
      is_default: values.is_default,
      level: finalLevel,
      name: values.templateName,
      parent_id: null,
      treatment_id: values.selectedTreatments?.[0]?.value || null,
      treatment_ids: (values.selectedTreatments || []).map(
        (treatment) => treatment.value
      ),
    };

    if (isGlobal) {
      axios
        .put(
          `/consent_clause/${selectedTemplate.id}/copy_global_template_to_private`,
          payload
        )
        .then((response) => {
          toast.success(
            response.data?.message || "New template added successfully!"
          );
          setIsGlobal(false);
          refetch?.();
          onSave?.(payload);

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        })
        .catch((error) => {
          console.error("Error updating template:", error);
          toast.error(error.response?.data?.detail || "Something went wrong.");
        });

      return;
    }

    if (!payload.is_default) {
      axios
        .put(`/consent_clause/${selectedTemplate.id}`, payload)
        .then(() => {
          toast.success("Template updated successfully!");
          refetch?.();
          onSave?.(payload);
        })
        .catch((error) => {
          console.error("Error updating template:", error);
          toast.error(error.response?.data?.detail || "Something went wrong.");
        });

      return;
    }

    const id = selectedTemplate?.id;
    const validationResponse = await validateConsentClause(payload, id);

    if (validationResponse.result === true) {
      axios
        .put(`/consent_clause/${id}`, payload)
        .then(() => {
          toast.success("Template updated successfully!");
          refetch?.();
          onSave?.(payload);
        })
        .catch((error) => {
          console.error("Error updating template:", error);
          toast.error("Failed to update template. Please try again.");
        });
    } else {
      let existingTreatments = "";

      if (Array.isArray(validationResponse.data)) {
        existingTreatments = validationResponse.data
          .map((item) => `<li>${item.consent_clause_name}</li>`)
          .join("\n");
      }

      const note = `<span>(If you continue, this consent clause will be made default and other default clauses for the selected treatments will be removed.)</span>`;
      const treatmentList = `<ul class="custom-list" style="text-align: left;">${existingTreatments}</ul>`;
      const title = `One or more selected treatments already have another default consent clause`;

      confirmAlert({
        title: <div dangerouslySetInnerHTML={{ __html: title }} />,
        message: (
          <div>
            <br />
            <div dangerouslySetInnerHTML={{ __html: treatmentList }} />
            <br />
            <hr />
            <div dangerouslySetInnerHTML={{ __html: note }} />
          </div>
        ),
        buttons: [
          {
            label: "Cancel",
            onClick: () => {},
          },
          {
            label: "Save Anyway",
            onClick: async () => {
              try {
                await axios.put(`/consent_clause/${id}`, payload);
                toast.success("Template updated successfully!");
                onSave?.(payload);
                refetch?.();
              } catch (error) {
                console.error("Error updating template:", error);
                toast.error("Failed to update template. Please try again.");
              }
            },
          },
        ],
      });
    }
  };

  const isDisabled =
    !isAdmin &&
    (selectedTemplate?.type === "intro" || selectedTemplate?.type === "outro");

  const levelOptions =
    userLevel === "practice"
      ? [
          { label: "User", value: "user" },
          { label: "Practice", value: "practice" },
        ]
      : [{ label: "User", value: "user" }];

  return (
    <Form
      onSubmit={handleSave}
      initialValues={{
        templateName,
        category,
        selectedTreatments,
        level:
          userLevel === "practice"
            ? selectedTemplate?.level || "user"
            : "user",
        is_default: selectedTemplate?.is_default || false,
      }}
      key={`${selectedTemplate?.id || "new"}-${isAdmin ? "admin" : "user"}`}
      render={({ handleSubmit }) => (
        <form className={styles.templateEditor} onSubmit={handleSubmit}>
          {selectedTemplate ? (
            <div className={styles.editorWrapper}>
              <div className={styles.editorContainer}>
                <div className={styles.topBar}>
                  <div>
                    <p className={styles.sectionTag}>Consent Clause Editor</p>
                    <h2 className={styles.title}>{selectedTemplate.name}</h2>
                    <p className={styles.subtitle}>
                      Edit content, category, treatments, and save changes.
                    </p>
                  </div>

                  {/* <button
                    type="button"
                    onClick={() => setShowGallery(!showGallery)}
                    className={styles.galleryToggleButton}
                  >
                    {showGallery ? "Close Gallery" : "Open Gallery"}
                  </button> */}
                </div>

                <div className={styles.formGrid}>
  <div className={styles.fieldBlock}>
    <label className={styles.fieldLabel}>Template Name</label>
    <input
      type="text"
      className={styles.inputField}
      value={templateName}
      onChange={handleTemplateNameChange}
      placeholder="Edit template name"
    />
  </div>

  {!(selectedTemplate?.type === "intro" || selectedTemplate?.type === "outro") && (
  <div className={styles.fieldBlock}>
    <label className={styles.fieldLabel}>Category</label>

    <select
      className={styles.dropdown}
      value={category || ""}
      onChange={handleCategoryChange}
      disabled={isGlobal}
    >
      <option value="">Select Category</option>
      {isCategoryLoading ? (
        <option disabled>Loading categories...</option>
      ) : (
        categoryData?.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.category_name}
          </option>
        ))
      )}
    </select>
  </div>
)}

  {!(selectedTemplate?.type === "intro" || selectedTemplate?.type === "outro") && (
    <div className={`${styles.fieldBlock} ${styles.treatmentFieldBlock}`}>
      <label className={styles.fieldLabel}>Treatments</label>

      <Autocomplete
        multiple
        options={treatmentOptions}
        value={selectedTreatments}
        onChange={handleTreatmentChange}
        getOptionLabel={(option) => option.label}
        loading={isTreatmentPlansLoading}
        className={styles.treatmentAutocomplete}
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select Treatments"
          />
        )}
      />
    </div>
  )}
</div>

                <div className={styles.editorBlock}>
                  <div className={styles.editorHeader}>
                    <span>Clause Content</span>
                    {!editorReady && (
                      <div className={styles.editorLoading}>
                        <CircularProgress size={18} />
                        <span>Loading editor...</span>
                      </div>
                    )}
                  </div>

                  <textarea
                    ref={editorRef}
                    className={styles.editorTextarea}
                    id="editor1"
                  />
                </div>

                <div className={styles.optionsRow}>
                  <div className={styles.radioBlock}>
                    <Radios
                      label="Level"
                      name="level"
                      required
                      data={levelOptions}
                      style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}
                    />
                  </div>

                  {!isGlobal && (
                    <div className={styles.checkboxBlock}>
                      <Checkboxes
                        name="is_default"
                        data={{ label: "Save as default", value: true }}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.actionRow}>
                  <Tooltip
                    title={
                      isDisabled
                        ? "Only an admin can save the intro and the outro"
                        : ""
                    }
                  >
                    <span>
                      <button
                        type="submit"
                        className={styles.saveButton}
                        disabled={isDisabled}
                      >
                        Save Changes
                      </button>
                    </span>
                  </Tooltip>
                </div>
              </div>

              {showGallery && (
                <div className={styles.imageGalleryContainer}>
                  <ImageGalleryV2
                    showGallery={showGallery}
                    onClose={() => setShowGallery(false)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyCard}>
                <div className={styles.emptyArrow}>←</div>
                <h3>Select a template</h3>
                <p>
                  Choose a consent template from the left panel to start editing.
                </p>
              </div>
            </div>
          )}
        </form>
      )}
    />
  );
};

export default TemplateEditor;